import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import { createTemplate, updateTemplate, DripEmailTemplate } from '@/actions/drip-campaigns';
import api from '@/utils/api/optimized-api';
import TiptapEmailEditor from './TiptapEmailEditor';

interface TemplateSlideFormProps {
  isOpen: boolean;
  onClose: () => void;
  template: DripEmailTemplate | null;
  onSuccess: () => void;
}

interface FieldMapping {
  leadField: string;
  displayName: string;
  variableName: string;
  default: string;
}

const CATEGORIES = [
  { value: 'welcome', label: 'Welcome' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'educational', label: 'Educational' },
  { value: 're_engagement', label: 'Re-engagement' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'other', label: 'Other' },
];


const TemplateSlideForm: React.FC<TemplateSlideFormProps> = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<{
    template_name: string;
    description: string;
    category: 'welcome' | 'nurture' | 'educational' | 're_engagement' | 'promotional' | 'other';
    subject_line: string;
    preheader_text: string;
    html_body: string;
    plain_text_body: string;
  }>({
    template_name: '',
    description: '',
    category: 'other',
    subject_line: '',
    preheader_text: '',
    html_body: '',
    plain_text_body: '',
  });

  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [_isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCrmColumns();
      if (template) {
        setFormData({
          template_name: template.template_name,
          description: template.description || '',
          category: template.category,
          subject_line: template.subject_line,
          preheader_text: template.preheader_text || '',
          html_body: template.html_body,
          plain_text_body: template.plain_text_body || '',
        });

        // Convert variables format to field mappings
        const mappings = template.variables.map((v: any) => ({
          leadField: v.leadField || '', // Load saved leadField mapping
          displayName: v.label,
          variableName: v.name,
          default: v.default,
        }));
        setFieldMappings(mappings);
      } else {
        // Reset form for new template
        setFormData({
          template_name: '',
          description: '',
          category: 'other',
          subject_line: '',
          preheader_text: '',
          html_body: '',
          plain_text_body: '',
        });
        setFieldMappings([]);
      }
    }
  }, [isOpen, template]);

  const loadCrmColumns = async () => {
    setIsLoadingColumns(true);
    try {
      await api.req({
        url: '/integrations/crm/lead-columns',
        method: api.Method.GET
      });
    } catch (error) {
      console.error('Error loading CRM columns:', error);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  const handleSave = async () => {
    // Validation
    if (!formData.template_name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!formData.subject_line.trim()) {
      toast.error('Subject line is required');
      return;
    }
    if (!formData.html_body.trim()) {
      toast.error('HTML body is required');
      return;
    }

    // Validate field mappings
    for (const mapping of fieldMappings) {
      if (!mapping.variableName.trim()) {
        toast.error('All field mappings must have a variable name');
        return;
      }
      if (!mapping.displayName.trim()) {
        toast.error('All field mappings must have a display name');
        return;
      }
    }

    try {
      setSaving(true);

      // Convert field mappings to variables format for backend
      const variables = fieldMappings.map(m => ({
        name: m.variableName,
        label: m.displayName,
        default: m.default,
        required: false,
        leadField: m.leadField, // Store lead field mapping
      }));

      const payload = {
        ...formData,
        variables,
        tags: [], // Remove tags functionality
      };

      if (template) {
        await updateTemplate(template.id, payload);
        toast.success('Template updated successfully');
      } else {
        await createTemplate(payload);
        toast.success('Template created successfully');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SettingsSlideModal
      isOpen={isOpen}
      onClose={onClose}
      title={template ? 'Edit Template' : 'Create New Template'}
      subtitle={template ? 'Update your email template' : 'Create a new email template for your drip campaigns'}
      icon={<Mail className="w-6 h-6" />}
    >
      <>
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={formData.template_name}
                  onChange={(e) => handleInputChange('template_name', e.target.value)}
                  placeholder="e.g., Welcome Email, Follow-up Sequence"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Brief description of this template's purpose"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value as typeof formData.category)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Email Content */}
            <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email Content</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject Line *
                </label>
                <input
                  type="text"
                  value={formData.subject_line}
                  onChange={(e) => handleInputChange('subject_line', e.target.value)}
                  placeholder="e.g. Follow-up for {{lead.name}}"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Preheader Text
                </label>
                <input
                  type="text"
                  value={formData.preheader_text}
                  onChange={(e) => handleInputChange('preheader_text', e.target.value)}
                  placeholder="Preview text shown in email clients"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  HTML Body *
                </label>
                <TiptapEmailEditor
                  value={formData.html_body}
                  onChange={(html) => handleInputChange('html_body', html)}
                  placeholder="Enter your email content. Use {{variable_name}} for dynamic fields."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Plain Text Body
                </label>
                <textarea
                  value={formData.plain_text_body}
                  onChange={(e) => handleInputChange('plain_text_body', e.target.value)}
                  placeholder="Plain text version (optional, for email clients that don't support HTML)"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-brandColor"
                />
              </div>
            </div>

            {/* Available Variables Reference */}
            <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Available Variables</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  These variables are automatically replaced with real data when the email is sent.
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Variable</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Replaced with</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {[
                      { variable: '{{lead.name}}', description: "Lead's full name" },
                      { variable: '{{lead.email}}', description: "Lead's email address" },
                      { variable: '{{lead.phone}}', description: "Lead's phone number" },
                      { variable: '{{lead.status}}', description: "Lead's current status" },
                      { variable: '{{client.company_name}}', description: 'Your company / account name' },
                    ].map(({ variable, description }) => (
                      <tr key={variable}>
                        <td className="px-4 py-2">
                          <code className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-mono">{variable}</code>
                        </td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-400 text-xs">{description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div></div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : 'Save Template'}</span>
              </button>
            </div>
          </div>
        </div>
      </>
    </SettingsSlideModal>
  );
};

export default TemplateSlideForm;
